import { create } from 'zustand'
import type { UserSettings } from './api'
import { ALL_UI_PACKS, RTL_LANGS } from '../i18n/packs'

const RTL = new Set<string>([
  ...RTL_LANGS,
  'ar',
  'he',
  'fa',
  'ur',
  'ps',
  'sd',
  'yi',
  'dv',
  'ckb',
  'ug',
])
const UI_PACKS = ALL_UI_PACKS

export type Theme = UserSettings['theme']
export type BackgroundId = UserSettings['background']
export type Density = UserSettings['list_density']
export type AiPrivacy = UserSettings['ai_privacy']

export type AccountPref = { color?: string | null; initial?: string | null; label?: string | null }

type UiState = {
  locale: string
  dir: 'ltr' | 'rtl'
  theme: Theme
  catalogCode: string
  background: BackgroundId
  fontSizePx: number
  listDensity: Density
  aiPrivacy: AiPrivacy
  a11yHighContrast: boolean
  a11yReduceMotion: boolean
  a11yComfort: boolean
  a11yVoiceConfirm: boolean
  accountPrefs: Record<string, AccountPref>
  settingsHydrated: boolean
  settingsOpen: boolean
  setLocale: (code: string) => void
  setTheme: (theme: Theme) => void
  setBackground: (id: BackgroundId) => void
  setFontSizePx: (n: number) => void
  setListDensity: (d: Density) => void
  setAiPrivacy: (m: AiPrivacy) => void
  setA11y: (patch: Partial<{
    highContrast: boolean
    reduceMotion: boolean
    comfort: boolean
    voiceConfirm: boolean
  }>) => void
  setAccountPref: (accountId: string, pref: AccountPref) => void
  applyServerSettings: (s: UserSettings) => void
  setSettingsHydrated: (v: boolean) => void
  setSettingsOpen: (v: boolean) => void
  toServerPatch: () => Partial<UserSettings>
}

function baseOf(code: string): string {
  return code.split('-')[0]?.toLowerCase() || 'en'
}

export const BG_PRESETS: Record<
  BackgroundId,
  { light: string; dark: string; swatch: string }
> = {
  lavender: {
    light: 'linear-gradient(135deg,#eef0ff 0%,#f7f4ff 55%,#eafaf6 100%)',
    dark: 'linear-gradient(135deg,#17162a,#0f1f1c)',
    swatch: 'linear-gradient(135deg,#eef0ff,#eafaf6)',
  },
  warm: {
    light: 'linear-gradient(135deg,#ffe9ec,#fff6e5)',
    dark: 'linear-gradient(135deg,#2a1620,#241d10)',
    swatch: 'linear-gradient(135deg,#ffe9ec,#fff6e5)',
  },
  sky: {
    light: 'linear-gradient(135deg,#e2f4ff,#eaf1ff)',
    dark: 'linear-gradient(135deg,#122334,#151b33)',
    swatch: 'linear-gradient(135deg,#e2f4ff,#eaf1ff)',
  },
  mint: {
    light: 'linear-gradient(135deg,#e8f7ea,#f4ffe8)',
    dark: 'linear-gradient(135deg,#14261a,#1c2612)',
    swatch: 'linear-gradient(135deg,#e8f7ea,#f4ffe8)',
  },
  neutral: {
    light: '#f4f2f7',
    dark: '#17161c',
    swatch: '#f4f2f7',
  },
}

export const useUiStore = create<UiState>((set, get) => ({
  locale: 'bs',
  catalogCode: 'bs',
  dir: 'ltr',
  theme: 'system',
  background: 'lavender',
  fontSizePx: 15,
  listDensity: 'standard',
  aiPrivacy: 'local',
  a11yHighContrast: false,
  a11yReduceMotion: false,
  a11yComfort: false,
  a11yVoiceConfirm: false,
  accountPrefs: {},
  settingsHydrated: false,
  settingsOpen: false,
  setLocale: (code) => {
    const base = baseOf(code)
    const pack = UI_PACKS.has(base) ? base : 'en'
    set({
      catalogCode: code,
      locale: pack,
      dir: RTL.has(base) ? 'rtl' : 'ltr',
    })
  },
  setTheme: (theme) => set({ theme }),
  setBackground: (background) => set({ background }),
  setFontSizePx: (fontSizePx) => set({ fontSizePx: Math.max(13, Math.min(20, fontSizePx)) }),
  setListDensity: (listDensity) => set({ listDensity }),
  setAiPrivacy: (aiPrivacy) => set({ aiPrivacy }),
  setA11y: (patch) =>
    set({
      a11yHighContrast: patch.highContrast ?? get().a11yHighContrast,
      a11yReduceMotion: patch.reduceMotion ?? get().a11yReduceMotion,
      a11yComfort: patch.comfort ?? get().a11yComfort,
      a11yVoiceConfirm: patch.voiceConfirm ?? get().a11yVoiceConfirm,
    }),
  setAccountPref: (accountId, pref) =>
    set((s) => ({
      accountPrefs: {
        ...s.accountPrefs,
        [accountId]: { ...(s.accountPrefs[accountId] || {}), ...pref },
      },
    })),
  applyServerSettings: (s) => {
    const base = baseOf(s.locale)
    const pack = UI_PACKS.has(base) ? base : 'en'
    set({
      catalogCode: s.locale,
      locale: pack,
      dir: RTL.has(base) ? 'rtl' : 'ltr',
      theme: s.theme,
      background: s.background,
      fontSizePx: s.font_size_px,
      listDensity: s.list_density,
      aiPrivacy: s.ai_privacy,
      a11yHighContrast: s.a11y_high_contrast,
      a11yReduceMotion: s.a11y_reduce_motion,
      a11yComfort: s.a11y_comfort,
      a11yVoiceConfirm: s.a11y_voice_confirm,
      accountPrefs: s.account_prefs || {},
      settingsHydrated: true,
    })
  },
  setSettingsHydrated: (settingsHydrated) => set({ settingsHydrated }),
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),
  toServerPatch: () => {
    const s = get()
    return {
      locale: s.catalogCode || s.locale,
      theme: s.theme,
      background: s.background,
      font_size_px: s.fontSizePx,
      list_density: s.listDensity,
      ai_privacy: s.aiPrivacy,
      a11y_high_contrast: s.a11yHighContrast,
      a11y_reduce_motion: s.a11yReduceMotion,
      a11y_comfort: s.a11yComfort,
      a11y_voice_confirm: s.a11yVoiceConfirm,
      account_prefs: s.accountPrefs,
    }
  },
}))
